import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { geocodeAddresses } from "@/lib/maps.functions";

export type MapPoint = {
  id: string;
  kind: "cafe" | "event";
  title: string;
  address: string;
  html: string;
  coords?: { lat: number; lng: number } | null;
};


const AGS = { lat: 21.8853, lng: -102.2916 };
const BROWSER_KEY = import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_MAPS_BROWSER_KEY as
  | string
  | undefined;
const TRACKING = import.meta.env.VITE_LOVABLE_CONNECTOR_GOOGLE_MAPS_TRACKING_ID as
  | string
  | undefined;

let loadPromise: Promise<void> | null = null;
function loadMaps(): Promise<void> {
  if (typeof window === "undefined") return Promise.reject(new Error("ssr"));
  const w = window as unknown as { google?: { maps?: unknown } };
  if (w.google?.maps) return Promise.resolve();
  if (loadPromise) return loadPromise;
  loadPromise = new Promise<void>((resolve, reject) => {
    (window as unknown as Record<string, unknown>).__bruloInitMap = () => resolve();
    const s = document.createElement("script");
    const channel = TRACKING ? `&channel=${TRACKING}` : "";
    s.src = `https://maps.googleapis.com/maps/api/js?key=${BROWSER_KEY}&loading=async&callback=__bruloInitMap${channel}`;
    s.async = true;
    s.onerror = () => reject(new Error("Failed to load Google Maps"));
    document.head.appendChild(s);
  });
  return loadPromise;
}

export function BruloMap({
  points,
  height = 500,
  zoom = 13,
}: {
  points: MapPoint[];
  height?: number;
  zoom?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const geocode = useServerFn(geocodeAddresses);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!BROWSER_KEY) {
      setError("Google Maps no está configurado.");
      return;
    }
    let cancelled = false;
    let markers: { setMap: (m: unknown) => void }[] = [];
    let map: any = null;

    const initMap = async () => {
      try {
        await loadMaps();
        if (cancelled || !ref.current) return;
        const g = (window as unknown as { google: any }).google;
        map = new g.maps.Map(ref.current, {
          center: AGS,
          zoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });

        const addrs = Array.from(
          new Set(
            points
              .filter((p) => !p.coords && !!p.address)
              .map((p) => p.address),
          ),
        );
        const { results } = addrs.length
          ? await geocode({ data: { addresses: addrs } })
          : { results: {} as Record<string, { lat: number; lng: number } | null> };
        if (cancelled) return;

        const info = new g.maps.InfoWindow();
        const bounds = new g.maps.LatLngBounds();
        let placed = 0;

        points.forEach((p) => {
          const loc = p.coords ?? results[p.address];
          if (!loc) return;
          const isCafe = p.kind === "cafe";
          const marker = new g.maps.Marker({
            position: loc,
            map,
            title: p.title,
            icon: {
              path: g.maps.SymbolPath.CIRCLE,
              fillColor: isCafe ? "#D85A30" : "#FFFFFF",
              fillOpacity: 1,
              strokeColor: isCafe ? "#5a1f0d" : "#1a1a1a",
              strokeWeight: 2,
              scale: isCafe ? 10 : 8,
            },
          });
          marker.addListener("click", () => {
            info.setContent(p.html);
            info.open({ map, anchor: marker });
          });
          markers.push(marker);
          bounds.extend(loc);
          placed += 1;
        });

        if (placed > 1) {
          map.fitBounds(bounds, 60);
        } else if (placed === 1) {
          map.setCenter(bounds.getCenter());
          map.setZoom(15);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Error cargando el mapa.");
        }
      }
    };

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        observer.disconnect();
        initMap();
      }
    });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      cancelled = true;
      observer.disconnect();
      markers.forEach((m) => m.setMap(null));
      markers = [];
    };
  }, [points, geocode, zoom]);

  if (error) {
    return (
      <div
        style={{ height }}
        className="bg-card border border-border rounded-lg flex items-center justify-center text-sm text-muted-foreground p-4 text-center"
      >
        {error}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      style={{ height }}
      className="rounded-lg border border-border overflow-hidden bg-card"
    />
  );
}

export function escapeHtml(s: string): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) =>
    c === "&"
      ? "&amp;"
      : c === "<"
        ? "&lt;"
        : c === ">"
          ? "&gt;"
          : c === '"'
            ? "&quot;"
            : "&#39;",
  );
}

// Only allow http(s) URLs in href attributes — blocks javascript:, data:, etc.
export function safeHttpUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.protocol === "https:" || u.protocol === "http:") return u.toString();
    return null;
  } catch {
    return null;
  }
}
